import { motion } from 'framer-motion'
import { Shield, Star, TrendingUp, RotateCcw, Wrench, MessageCircle, Flame, Heart, BookOpen, Compass, Users } from 'lucide-react'
import { CalendlyBtn } from '@/components/ui/calendly-btn'
import { Link } from 'react-router-dom'

const frameworks = [
  {
    icon: Shield,
    name: 'The Strategic Parts Framework',
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
    body: 'Based on the Internal Family Systems (IFS) model, this framework helps you work with the different parts of yourself—not just the loudest voice in the room. You\'ll learn to identify your protectors (the parts trying to keep you safe), build trust between them and your core Self, and use your system strategically—not just reactively—to pursue meaningful goals. When your inner team is aligned, you move forward faster—with less friction and more confidence.',
  },
  {
    icon: Star,
    name: 'The Six Elements of Desire',
    color: 'text-violet-600 dark:text-violet-400',
    bg: 'bg-violet-50 dark:bg-violet-900/20',
    border: 'border-violet-200 dark:border-violet-800',
    body: 'This model helps you unpack the deeper architecture of your longing and motivation. Whether you\'re chasing a goal, recovering from burnout, or redefining success, we break it down into six parts: Desire (what you want), Goals (how you measure progress), Needs (what must be met to stay energized), Values (what matters most), Beliefs (what you hold as true), and Identity (who you think you are). Clarity here changes everything.',
  },
  {
    icon: TrendingUp,
    name: 'The Change Curve',
    subtitle: 'aka The Call to Change',
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    border: 'border-emerald-200 dark:border-emerald-800',
    body: 'Inspired by Joseph Campbell\'s Hero\'s Journey—and tailored for real life. This is the arc most of us walk when something in us says: "This can\'t go on." It maps the stages of transformation: the comfort zone you\'ve outgrown, the resistance and fear, the breakthrough, and the integration of new truth into daily life. It helps you see where you are and what\'s ahead.',
  },
  {
    icon: RotateCcw,
    name: 'The Stages of Acceptance',
    color: 'text-rose-600 dark:text-rose-400',
    bg: 'bg-rose-50 dark:bg-rose-900/20',
    border: 'border-rose-200 dark:border-rose-800',
    body: 'Growth isn\'t linear—especially when it involves loss, change, or truth you\'re not ready to face. This modification of the Kübler-Ross stages helps normalize and track your emotional process: Denial, Defense, Bargain, Breakdown, Breakthrough, Integration. Not everyone hits every stage. But most of us visit a few more than once.',
    stages: ['Denial: "This isn\'t happening."', 'Defense: "I\'ll just push through."', 'Bargain: "Maybe if I change this it won\'t be so bad."', 'Breakdown: "It\'s not working."', 'Breakthrough: "Something has to change."', 'Integration: "This is my new reality—and I can work with it."'],
  },
  {
    icon: Wrench,
    name: 'Tools from Other Disciplines',
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800',
    body: 'Some models aren\'t mine, but I borrow them when they fit. Clients often find these useful:',
    tools: [
      { name: 'The Eisenhower Matrix', desc: 'Helps sort what\'s urgent vs. what\'s truly important' },
      { name: 'Atomic Habits Framework', desc: 'Understand how habits form and how to reshape them' },
      { name: 'Ikigai', desc: 'A beautiful overlap of what you love, what you\'re good at, what the world needs, and what you can be paid for' },
      { name: 'The Enneagram / MBTI', desc: 'Helpful personality models when used as mirrors, not boxes' },
    ],
  },
  {
    icon: MessageCircle,
    name: 'How I Use These in Coaching',
    color: 'text-slate-600 dark:text-slate-400',
    bg: 'bg-slate-50 dark:bg-slate-900/20',
    border: 'border-slate-200 dark:border-slate-700',
    body: 'I don\'t force frameworks. I use them when they help. Some clients love structure; others need space to wander first. What matters most is this: each of these models is a way to help you hear yourself more clearly, understand your parts more compassionately, and move forward with more intention.',
  },
]

export default function ModelsFrameworksPage() {
  return (
    <div className="bg-white dark:bg-slate-950 min-h-screen">

      <div className="bg-slate-900 pt-28 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="text-blue-400 text-sm font-semibold tracking-widest uppercase mb-3">
            The Approach
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Models & Frameworks
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="text-slate-400 text-lg max-w-2xl">
            Tools for self-understanding, clarity, and change. Not rigid formulas—lenses. And the right lens can make all the difference.
          </motion.p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">

        <div className="max-w-3xl mb-16">
          <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-xl text-slate-600 dark:text-slate-300 leading-relaxed mb-5">
            We all get stuck sometimes—caught between options, overwhelmed by decisions, or unsure what's really driving our dissatisfaction. That's where models and frameworks can help.
          </motion.p>
          <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.08 }}
            className="text-slate-600 dark:text-slate-400 leading-relaxed">
            They don't tell you what to do. They help you see—your patterns, your parts, your values, and your path forward.
          </motion.p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mb-20">
          {frameworks.map(({ icon: Icon, name, subtitle, color, bg, border, body, stages, tools }, i) => (
            <motion.div key={name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: (i % 4) * 0.08 }}
              className={`rounded-2xl border p-7 flex flex-col gap-4 ${bg} ${border}`}>
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${bg}`}>
                  <Icon size={18} className={color} />
                </div>
                <div>
                  <h3 className={`font-bold ${color} text-base leading-snug`}>{name}</h3>
                  {subtitle && <p className="text-xs text-slate-400 mt-0.5 italic">{subtitle}</p>}
                </div>
              </div>
              <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">{body}</p>
              {stages && (
                <ul className="space-y-1">
                  {stages.map((s, si) => (
                    <li key={si} className="text-xs text-slate-500 dark:text-slate-400 flex gap-2">
                      <span className={`mt-0.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${color.replace('text-', 'bg-')}`} style={{ marginTop: '5px' }} />
                      {s}
                    </li>
                  ))}
                </ul>
              )}
              {tools && (
                <ul className="space-y-2">
                  {tools.map((t) => (
                    <li key={t.name} className="text-xs text-slate-500 dark:text-slate-400">
                      <span className={`font-semibold ${color}`}>{t.name}</span> — {t.desc}
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          ))}
        </div>

        {/* ── Placeholder section — for Douglas to review ─────────────────── */}
        <div className="relative border-2 border-dashed border-amber-300 dark:border-amber-700 rounded-3xl p-8 mb-20">
          <div className="absolute -top-4 left-6">
            <span className="bg-amber-50 dark:bg-amber-900/60 text-amber-700 dark:text-amber-300 text-xs font-semibold px-3 py-1.5 rounded-full border border-amber-300 dark:border-amber-700">
              Draft Content — For Douglas to review &amp; accept
            </span>
          </div>
          <p className="text-xs text-amber-600 dark:text-amber-400 mb-6 mt-2">The additional framework concepts below were drafted as placeholders. Douglas — please keep, modify, or remove as you see fit.</p>
          <div className="grid sm:grid-cols-2 gap-5">
            {[
              { icon: Shield, name: 'Internal Family Systems (IFS)', author: 'Dr. Richard Schwartz', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800', body: 'The primary framework. IFS holds that we are all made up of multiple "parts" — each with its own perspective, intention, and history. The goal is to heal the parts that carry burdens and restore the leadership of the core Self. Widely researched and recognized as an evidence-based practice.', link: '/ifs', linkLabel: 'Learn more about IFS' },
              { icon: Compass, name: 'Narrative Approaches', author: 'White & Epston tradition', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200 dark:border-emerald-800', body: 'The stories we tell about ourselves shape everything. Narrative approaches help us recognize that the dominant story is never the whole story — and that we have authorship over the next chapter. Particularly useful in career transitions and identity work.', link: null, linkLabel: null },
              { icon: Users, name: 'Adult Development Theory', author: 'Kegan, Torbert & others', color: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-50 dark:bg-violet-900/20', border: 'border-violet-200 dark:border-violet-800', body: "Adults don't stop developing — but we can get stuck at a given stage. Frameworks from developmental psychology help identify where you're operating from and what expanding your perspective could look like. Especially relevant for leaders and high performers.", link: null, linkLabel: null },
              { icon: Heart, name: 'Values-Based Coaching', author: 'Core to coaching practice', color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-50 dark:bg-rose-900/20', border: 'border-rose-200 dark:border-rose-800', body: "When your actions aren't aligned with your actual values, something always feels off — even when everything looks good on the outside. Values clarification isn't a one-time exercise. It's an ongoing calibration that shapes every coaching conversation.", link: null, linkLabel: null },
              { icon: Flame, name: 'Somatic Awareness', author: 'Body-based coaching tools', color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-200 dark:border-orange-800', body: "The body keeps score. Somatic awareness helps clients notice where tension, resistance, or emotion lives in the body — and use that information as data in the coaching process. Used lightly within a coaching (not therapeutic) frame.", link: null, linkLabel: null },
              { icon: BookOpen, name: 'Strengths & Appreciative Inquiry', author: 'Positive psychology tradition', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-800', body: "Change doesn't have to be driven by what's broken. Appreciative approaches focus on what's already working — and how to amplify it. Used alongside IFS to help clients access and trust their own resources.", link: null, linkLabel: null },
            ].map(({ icon: Icon, name, author, color, bg, border, body, link, linkLabel }) => (
              <div key={name} className={`rounded-2xl border p-6 flex flex-col gap-3 ${bg} ${border}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${bg}`}>
                    <Icon size={16} className={color} />
                  </div>
                  <div>
                    <h3 className={`font-bold ${color} text-sm leading-snug`}>{name}</h3>
                    <p className="text-xs text-slate-400 mt-0.5">{author}</p>
                  </div>
                </div>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">{body}</p>
                {link && (
                  <Link to={link} className={`inline-flex items-center gap-1 text-xs font-medium mt-auto ${color} hover:underline`}>
                    {linkLabel}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="bg-slate-900 rounded-3xl p-10 text-center">
          <p className="text-blue-400 text-xs font-semibold uppercase tracking-widest mb-3">Experience It Firsthand</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Frameworks only go so far on paper.</h2>
          <p className="text-slate-400 max-w-xl mx-auto mb-8 leading-relaxed">
            Let's explore which model might bring clarity to your turning point. A free consultation gives you a direct sense of how this work actually feels.
          </p>
          <CalendlyBtn size="lg" className="bg-blue-600 hover:bg-blue-700 text-white border-transparent rounded-full hover:scale-105 hover:shadow-lg hover:shadow-blue-600/25 transition-all">
            Book Free Consultation
          </CalendlyBtn>
        </motion.div>
      </div>
    </div>
  )
}
